'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Plus, Search, X, Edit2, Trash2,
  Loader2, Calendar, Scale, ImageIcon,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { rawMaterialsService, type RawMaterial, type MaterialUnit } from '@/lib/supabase/services';
import ImageUpload from '@/components/ui/ImageUpload';
import AppImage from '@/components/ui/AppImage';

const UNITS: MaterialUnit[] = ['كجم', 'طن', 'لتر', 'متر', 'قطعة', 'صندوق', 'كيس', 'برميل'];

function formatDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const EMPTY_FORM = { name: '', quantity: '', unit: 'طن' as MaterialUnit, imageUrl: '', addedDate: new Date().toISOString().split('T')[0], notes: '' };

export default function RawMaterialsContent() {
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<RawMaterial | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [viewImage, setViewImage] = useState<{ url: string; name: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await rawMaterialsService.getAll();
      setMaterials(data);
    } catch (err: any) {
      toast.error('فشل تحميل البيانات: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = materials.filter((m) =>
    !searchQuery || m.name.includes(searchQuery) || m.unit.includes(searchQuery)
  );

  const totalTons = materials.filter((m) => m.unit === 'طن').reduce((sum, m) => sum + m.quantity, 0);

  const openAdd = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item: RawMaterial) => {
    setEditingItem(item);
    setForm({ name: item.name, quantity: String(item.quantity), unit: item.unit, imageUrl: item.imageUrl, addedDate: item.addedDate, notes: item.notes });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quantity) { toast.error('يرجى ملء الحقول المطلوبة'); return; }
    const qty = parseFloat(form.quantity);
    if (isNaN(qty) || qty < 0) { toast.error('الكمية يجب أن تكون رقماً صحيحاً'); return; }
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), quantity: qty, unit: form.unit, imageUrl: form.imageUrl, addedDate: form.addedDate, notes: form.notes };
      if (editingItem) {
        await rawMaterialsService.update(editingItem.id, payload);
        toast.success('تم تحديث المادة الخام بنجاح');
      } else {
        await rawMaterialsService.create(payload);
        toast.success('تم إضافة المادة الخام بنجاح');
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      toast.error('فشل الحفظ: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`هل أنت متأكد من حذف "${name}"؟`)) return;
    try {
      await rawMaterialsService.delete(id);
      toast.success('تم حذف المادة');
      loadData();
    } catch (err: any) {
      toast.error('فشل الحذف: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-info/15 border border-info/30 flex items-center justify-center">
            <Package size={20} className="text-info" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">إدارة المواد الخام</h1>
            <p className="text-sm text-muted-foreground">متابعة مخزون المواد الخام للمصنع</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all active:scale-95"
        >
          <Plus size={16} />
          إضافة مادة خام
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي المواد</p>
          <p className="text-3xl font-bold text-foreground tabular-nums">{materials.length}</p>
        </div>
        <div className="bg-card border border-info/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي الأطنان</p>
          <p className="text-3xl font-bold text-info tabular-nums">{totalTons.toFixed(1)}</p>
        </div>
        <div className="bg-card border border-success/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">مضافة اليوم</p>
          <p className="text-3xl font-bold text-success tabular-nums">
            {materials.filter((m) => m.addedDate === new Date().toISOString().split('T')[0]).length}
          </p>
        </div>
        <div className="bg-card border border-warning/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">بصور مرفوعة</p>
          <p className="text-3xl font-bold text-warning tabular-nums">{materials.filter((m) => m.imageUrl).length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="بحث باسم المادة أو الوحدة..."
          className="w-full bg-card border border-border rounded-xl pr-9 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="text-accent animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">الصورة</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">اسم المادة</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">الكمية</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">الوحدة</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">تاريخ الإضافة</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">ملاحظات</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16 text-muted-foreground">
                      <Package size={36} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">لا توجد مواد خام مسجلة</p>
                    </td>
                  </tr>
                ) : filtered.map((item) => (
                  <tr key={item.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div
                        className="w-12 h-12 rounded-xl overflow-hidden border border-border bg-muted flex items-center justify-center cursor-pointer"
                        onClick={() => item.imageUrl && setViewImage({ url: item.imageUrl, name: item.name })}
                      >
                        {item.imageUrl ? (
                          <AppImage src={item.imageUrl} alt={`صورة ${item.name}`} className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon size={16} className="text-muted-foreground" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-foreground text-sm">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-foreground tabular-nums font-bold">{item.quantity.toLocaleString('ar-EG')}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-info/10 text-info text-xs font-semibold border border-info/20">
                        <Scale size={10} />
                        {item.unit}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={12} />
                        {formatDate(item.addedDate)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-[150px] truncate">{item.notes || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(item.id, item.name)} className="p-1.5 rounded-lg text-danger hover:bg-danger/10 transition-all">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="font-bold text-foreground">{editingItem ? 'تعديل المادة الخام' : 'إضافة مادة خام جديدة'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">اسم المادة *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="مثال: يوريا، حامض الفوسفوريك..."
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">الكمية *</label>
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                    placeholder="0.000"
                    min="0"
                    step="0.001"
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">الوحدة *</label>
                  <select
                    value={form.unit}
                    onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value as MaterialUnit }))}
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">تاريخ الإضافة *</label>
                <input
                  type="date"
                  value={form.addedDate}
                  onChange={(e) => setForm((p) => ({ ...p, addedDate: e.target.value }))}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <ImageUpload
                bucket="materials"
                currentUrl={form.imageUrl}
                onUpload={(url) => setForm((p) => ({ ...p, imageUrl: url }))}
                label="صورة المادة"
                folder="raw-materials"
              />
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">ملاحظات</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  placeholder="ملاحظات إضافية..."
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {editingItem ? 'حفظ التعديلات' : 'إضافة المادة'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 bg-muted text-muted-foreground rounded-xl font-medium text-sm hover:text-foreground transition-all">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image viewer */}
      {viewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setViewImage(null)}>
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewImage(null)} className="absolute -top-10 left-0 text-white/70 hover:text-white">
              <X size={24} />
            </button>
            <p className="text-white text-center mb-3 font-semibold">{viewImage.name}</p>
            <AppImage src={viewImage.url} alt={viewImage.name} className="w-full rounded-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
